import React, { useMemo, ReactNode } from 'react';
import { useAppContext } from '@prisjakt/ui-app-context';
import Panel from '@prisjakt/ui-panel';
import { ScrollLink, ScrollElement } from '@prisjakt/ui-scroll';
import { ProductsSlice } from '../../../../types';
import { LayoutMode } from '../../types';
import Pagination from './pagination';
import ProductsHeader from './products-header';
import * as S from './styles';

type Props = Pick<
  ProductsSlice,
  | 'id'
  | 'pageInfo'
  | 'productsCount'
  | 'storeCount'
  | 'legalText'
  | 'availableSorts'
  | 'currentSort'
  | 'maxHitsAmount'
> & {
  children: ReactNode;
  layout: LayoutMode;
  onLayoutChange: (layout: LayoutMode) => void;
  compareProductsToggle?: ReactNode;
  availableSortsTranslations: Record<string, string>;
  supportedViewModes: ReadonlyArray<LayoutMode>;
  displayProductSearch?: boolean;
};

const ProductOffersContainer = ({
  pageInfo,
  productsCount,
  storeCount,
  legalText,
  availableSorts,
  availableSortsTranslations,
  currentSort,
  onLayoutChange,
  layout,
  children,
  maxHitsAmount,
  compareProductsToggle,
  supportedViewModes,
  displayProductSearch,
}: Props) => {
  const { t } = useAppContext();
  const headerElement = useMemo(
    () => (
      <ProductsHeader
        layout={layout}
        setLayout={onLayoutChange}
        productsCount={productsCount}
        storeCount={storeCount}
        legalText={legalText}
        availableSorts={availableSorts}
        availableSortsTranslations={availableSortsTranslations}
        currentSort={currentSort}
        compareProductsToggle={compareProductsToggle}
        supportedViewModes={supportedViewModes}
        displayProductSearch={displayProductSearch}
      />
    ),
    [
      productsCount,
      storeCount,
      legalText,
      availableSorts,
      availableSortsTranslations,
      currentSort,
      layout,
      onLayoutChange,
      compareProductsToggle,
      supportedViewModes,
      displayProductSearch,
    ]
  );

  const footerElement = useMemo(
    () => (
      <ScrollLink to="Products">
        <Pagination
          pageInfo={pageInfo}
          total={Math.min(productsCount, maxHitsAmount)}
        />
      </ScrollLink>
    ),
    [pageInfo, productsCount, maxHitsAmount]
  );
  const showTooManyHitsNotice =
    productsCount > maxHitsAmount &&
    (pageInfo.offset || 0) + (pageInfo.limit || 0) >= maxHitsAmount;

  return (
    <ScrollElement name="Products">
      <S.Wrapper>
        <Panel header={headerElement} footer={footerElement}>
          {children}

          {showTooManyHitsNotice && (
            <S.Notice>
              {t('category', 'product-collection.too-many-hits-notice', {
                maxHitsAmount,
              })}
            </S.Notice>
          )}
        </Panel>
      </S.Wrapper>
    </ScrollElement>
  );
};

export default ProductOffersContainer;
